// Canvas Renderer Module
// Handles all drawing operations on the HTML5 canvas

import { calculateMarkdownHeight } from './markdownParser.js';
import { renderMarkdownToCanvas } from './markdownRenderer.js';
import { DEFAULT_GLOBAL_FONT_SIZE } from './CanvasState.js';

// Main rendering function for the modular canvas system
export function renderCanvas(ctx, canvas, canvasState, inputHandler, uiManager) {
    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context and apply transformations
    ctx.save();
    ctx.translate(canvasState.offsetX, canvasState.offsetY);
    ctx.scale(canvasState.scale, canvasState.scale);

    // Draw grid
    drawGrid(ctx, canvasState.offsetX, canvasState.offsetY, canvasState.scale, canvas.width, canvas.height);

    // Update visible nodes for viewport culling
    updateVisibleNodes(canvasState, canvas);

    // Draw connections first (behind nodes)
    const visibleNodeIds = new Set(Array.from(canvasState.visibleNodes).map(n => n.id && n.id.toString()));
    const visibleConnections = canvasState.connections.filter(connection => {
        // Handle both old and new connection formats during transition
        const fromNodeId = connection.fromNode || connection.from;
        const toNodeId = connection.toNode || connection.to;
        return fromNodeId && toNodeId && 
               (visibleNodeIds.has(fromNodeId.toString()) || visibleNodeIds.has(toNodeId.toString()));
    });
    
    visibleConnections.forEach(connection => {
        const isSelected = canvasState.selectedConnection && canvasState.selectedConnection.id === connection.id;
        drawConnection(ctx, connection, canvasState.nodes, isSelected);
    });

    // Draw visible nodes
    const activeThreadId = canvasState.activeThreadId ? canvasState.activeThreadId.toString() : null;
    canvasState.visibleNodes.forEach(node => {
        // Show connection points for selected nodes or when hovering near connection areas
        const showConnectionPoints =
            node.isSelected ||
            (inputHandler.hoveredNode === node &&
             !inputHandler.isConnecting && 
             !inputHandler.isDragging && 
             !inputHandler.isResizing &&
             isHoveringNearConnectionPoint(inputHandler, node, canvasState));
        
        const showResizeHandles = node.isSelected && !showConnectionPoints;
        
        // Pass hover state information to drawNode for enhanced connection point rendering
        const nodeHoverInfo = {
            isHovered: inputHandler.hoveredNode === node,
            isConnecting: inputHandler.isConnecting,
            hoveredConnectionPoint: null
        };
        
        // Detect which specific connection point is being hovered
        if (nodeHoverInfo.isHovered && !inputHandler.isDragging && !inputHandler.isResizing) {
            const canvasX = (inputHandler.lastMouseX - canvasState.offsetX) / canvasState.scale;
            const canvasY = (inputHandler.lastMouseY - canvasState.offsetY) / canvasState.scale;
            nodeHoverInfo.hoveredConnectionPoint = getConnectionPointAt(node, canvasX, canvasY);
        }
        
        const nodeId = node.id ? node.id.toString() : null;
        const nodeThreadId = node.threadId ? node.threadId.toString() : null;
        const isThreadRoot = Boolean(activeThreadId && nodeId && nodeId === activeThreadId);
        const isActiveThreadNode = Boolean(
            activeThreadId &&
            (isThreadRoot || (nodeThreadId && nodeThreadId === activeThreadId))
        );

        drawNode(ctx, node, showConnectionPoints, showResizeHandles, nodeHoverInfo, {
            isActiveThreadNode,
            isThreadRoot
        });

        // Highlight potential connection target when connecting
        if (inputHandler.isConnecting && inputHandler.hoveredNode === node && node !== inputHandler.connectionStart) {
            ctx.strokeStyle = '#10b981';
            ctx.lineWidth = 3;
            ctx.setLineDash([5, 5]);
            ctx.strokeRect(node.x - 5, node.y - 5, node.width + 10, node.height + 10);
            ctx.setLineDash([]);
        }

        // Draw generated content (images/videos)
        drawGeneratedContent(ctx, node, canvas, canvasState);
    });

    // Draw connection preview if connecting
    if (inputHandler.isConnecting && inputHandler.connectionStart) {
        drawConnectionPreview(
            ctx, 
            inputHandler.connectionStart, 
            inputHandler.lastMouseX || 0, 
            inputHandler.lastMouseY || 0, 
            canvasState.offsetX, 
            canvasState.offsetY, 
            canvasState.scale, 
            inputHandler.connectionStartPoint
        );
    }

    // Draw selection rectangle if selecting
    if (inputHandler.isSelecting) {
        drawSelectionRectangle(ctx, inputHandler.selectionStart, inputHandler.selectionEnd);
    }

    // Restore context
    ctx.restore();

    // Update UI elements that depend on canvas state
    updateUIElements(canvasState, canvas, uiManager);
}

// Helper function to check if mouse is hovering near connection points
function isHoveringNearConnectionPoint(inputHandler, node, canvasState) {
    if (!inputHandler.lastMouseX || !inputHandler.lastMouseY) return false;

    const canvasX = (inputHandler.lastMouseX - canvasState.offsetX) / canvasState.scale;
    const canvasY = (inputHandler.lastMouseY - canvasState.offsetY) / canvasState.scale;

    const connectionPoint = getConnectionPointAt(node, canvasX, canvasY);
    return connectionPoint !== null;
}

// Update visible nodes for viewport culling
function updateVisibleNodes(canvasState, canvas) {
    const viewport = getViewportBounds(canvasState, canvas);
    canvasState.visibleNodes.clear();

    canvasState.nodes.forEach(node => {
        if (!canvasState.isNodeInActiveThread(node)) {
            return;
        }
        if (isNodeVisible(node, viewport)) {
            canvasState.visibleNodes.add(node);
        }
    });
}

// Calculate viewport bounds in canvas coordinates
function getViewportBounds(canvasState, canvas) {
    const left = -canvasState.offsetX / canvasState.scale;
    const top = -canvasState.offsetY / canvasState.scale;
    const right = left + canvas.width / canvasState.scale;
    const bottom = top + canvas.height / canvasState.scale;

    return { left, top, right, bottom };
}

// Check if node is visible in current viewport
function isNodeVisible(node, viewport) {
    return !(node.x + node.width < viewport.left ||
            node.x > viewport.right ||
            node.y + node.height < viewport.top ||
            node.y > viewport.bottom);
}

// Draw generated content (images and videos)
function drawGeneratedContent(ctx, node, canvas, canvasState) {
    const isMediaRef = node.isMediaReference;

    // Draw generated image thumbnail if present
    if (node.generatedImageUrl) {
        if (!node._imgObj || node._imgObj.src !== node.generatedImageUrl) {
            node._imgObj = new window.Image();
            node._imgObj.src = node.generatedImageUrl;
            node._imgObj.crossOrigin = 'anonymous'; // Handle CORS for external images
            node._imgObj.onload = () => {
                // Update node dimensions for media references once image is loaded
                if (isMediaRef) {
                    const maxDim = 320;
                    const aspect = node._imgObj.naturalWidth / node._imgObj.naturalHeight;
                    if (aspect >= 1) {
                        node.width = Math.min(maxDim, node._imgObj.naturalWidth);
                        node.height = node.width / aspect;
                    } else {
                        node.height = Math.min(maxDim, node._imgObj.naturalHeight);
                        node.width = node.height * aspect;
                    }
                }
            };
            node._imgObj.onerror = () => {
                // Fallback for images that fail to load
                console.warn('Failed to load image:', node.generatedImageUrl);
            };
        }
        if (node._imgObj.complete && node._imgObj.naturalWidth > 0) {
            // For media references draw the image directly inside the node bounds
            if (isMediaRef) {
                const headerHeight = node._headerHeight || 50;
                const imageY = node.y + headerHeight;
                const imageHeight = node.height - headerHeight;
                ctx.drawImage(node._imgObj, node.x, imageY, node.width, imageHeight);
            } else {
                const maxWidth = Math.min(320, node.width - 20);
                const maxHeight = 240;
                const originalWidth = node._imgObj.naturalWidth;
                const originalHeight = node._imgObj.naturalHeight;
                const aspectRatio = originalWidth / originalHeight;
                let thumbWidth, thumbHeight;
                if (aspectRatio > maxWidth / maxHeight) {
                    thumbWidth = maxWidth;
                    thumbHeight = maxWidth / aspectRatio;
                } else {
                    thumbHeight = maxHeight;
                    thumbWidth = maxHeight * aspectRatio;
                }
                const imgX = node.x + (node.width - thumbWidth) / 2;
                const imgY = node.y + node.height + 10;
                ctx.drawImage(node._imgObj, imgX, imgY, thumbWidth, thumbHeight);
            }
        }
    }

    // Handle video positioning (videos and iframes are positioned absolutely in DOM)
    if (node.generatedVideoUrl) {
        // Handle YouTube and other platform videos with iframe
        if (node.isYouTubeVideo || isVideoUrl(node.generatedVideoUrl)) {
            if (!node._iframeObj || node._iframeObj.src !== getEmbedUrl(node.generatedVideoUrl)) {
                // Remove old iframe if it exists and is still in the DOM
                if (node._iframeObj) {
                    try {
                        if (node._iframeObj.parentNode) {
                            node._iframeObj.parentNode.removeChild(node._iframeObj);
                        }
                    } catch (e) {
                        console.warn('Failed to remove iframe:', e);
                    }
                    node._iframeObj = null;
                }
                
                node._iframeObj = document.createElement('iframe');
                node._iframeObj.src = getEmbedUrl(node.generatedVideoUrl);
                node._iframeObj.frameBorder = '0';
                node._iframeObj.allowFullscreen = true;
                node._iframeObj.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
                node._iframeObj.style.position = 'absolute';
                node._iframeObj.style.pointerEvents = 'auto';
                node._iframeObj.style.zIndex = '1000';
                node._iframeObj.style.border = 'none';
                node._iframeObj.style.borderRadius = '0 0 8px 8px'; // Rounded bottom corners only
                document.body.appendChild(node._iframeObj);
            }
            
            // Position the iframe below the header for media references
            const rect = canvas.getBoundingClientRect();
            const headerHeight = node._headerHeight || 50;
            
            // Limit iframe scaling to prevent controls from becoming too large
            // Cap the effective scale at 1.5x for better usability
            const maxVideoScale = 1.5;
            const effectiveScale = Math.min(canvasState.scale, maxVideoScale);
            
            const iframeX = rect.left + node.x * canvasState.scale + canvasState.offsetX;
            const iframeY = rect.top + (node.y + (isMediaRef ? headerHeight : node.height + 10)) * canvasState.scale + canvasState.offsetY;
            const iframeWidth = node.width * effectiveScale;
            const iframeHeight = (node.height - (isMediaRef ? headerHeight : 0)) * effectiveScale;
            
            if (node._iframeObj && node._iframeObj.style) {
                node._iframeObj.style.left = iframeX + 'px';
                node._iframeObj.style.top = iframeY + 'px';
                node._iframeObj.style.width = iframeWidth + 'px';
                node._iframeObj.style.height = iframeHeight + 'px';
                node._iframeObj.style.display = 'block';
            }
        }
        // Handle regular video files
        else {
            if (!node._videoObj || node._videoObj.src !== node.generatedVideoUrl) {
                node._videoObj = document.createElement('video');
                node._videoObj.src = node.generatedVideoUrl;
                node._videoObj.muted = true;
                node._videoObj.loop = true;
                node._videoObj.controls = true;
                node._videoObj.preload = 'metadata';
                node._videoObj.style.position = 'absolute';
                node._videoObj.style.pointerEvents = 'auto';
                node._videoObj.style.zIndex = '1000';
                node._videoObj.style.borderRadius = '0 0 8px 8px';
                document.body.appendChild(node._videoObj);
            }
            
            if (node._videoObj.readyState >= 1) {
                const rect = canvas.getBoundingClientRect();
                const headerHeight = node._headerHeight || 50;
                
                // Limit video scaling to prevent controls from becoming too large
                // Cap the effective scale at 1.5x for better usability
                const maxVideoScale = 1.5;
                const effectiveScale = Math.min(canvasState.scale, maxVideoScale);
                
                // Position video below the header for media references, or below the node for regular nodes
                const videoX = rect.left + node.x * canvasState.scale + canvasState.offsetX;
                const videoY = rect.top + (node.y + (isMediaRef ? headerHeight : node.height + 10)) * canvasState.scale + canvasState.offsetY;
                const videoWidth = node.width * effectiveScale;
                const videoHeight = (node.height - (isMediaRef ? headerHeight : 0)) * effectiveScale;
                
                if (node._videoObj.style) {
                    node._videoObj.style.left = videoX + 'px';
                    node._videoObj.style.top = videoY + 'px';
                    node._videoObj.style.width = videoWidth + 'px';
                    node._videoObj.style.height = videoHeight + 'px';
                    node._videoObj.style.display = 'block';
                }
            } else {
                if (node._videoObj && node._videoObj.style) {
                    node._videoObj.style.display = 'none';
                }
            }
        }
    }
}

// Helper function to check if URL is from a video platform
function isVideoUrl(url) {
    const videoSites = [
        'vimeo.com',
        'dailymotion.com',
        'twitch.tv',
        'tiktok.com',
        'instagram.com',
        'twitter.com',
        'x.com'
    ];
    
    try {
        const urlObj = new URL(url);
        return videoSites.some(site => urlObj.hostname.includes(site));
    } catch {
        return false;
    }
}

// Helper function to convert various video URLs to embeddable URLs
function getEmbedUrl(url) {
    try {
        const urlObj = new URL(url);
        
        // YouTube
        if (urlObj.hostname.includes('youtube.com')) {
            const videoId = urlObj.searchParams.get('v');
            if (videoId) {
                return `https://www.youtube.com/embed/${videoId}`;
            }
        } else if (urlObj.hostname.includes('youtu.be')) {
            const videoId = urlObj.pathname.slice(1);
            if (videoId) {
                return `https://www.youtube.com/embed/${videoId}`;
            }
        }
        
        // Vimeo
        else if (urlObj.hostname.includes('vimeo.com')) {
            const videoId = urlObj.pathname.split('/').pop();
            if (videoId) {
                return `https://player.vimeo.com/video/${videoId}`;
            }
        }
        
        // For other platforms, return the original URL (may not always work)
        return url;
    } catch {
        return url;
    }
}

// Update UI elements that depend on canvas state
function updateUIElements(canvasState, canvas, uiManager) {
    // Hide videos and iframes for nodes that are not visible
    canvasState.nodes.forEach(node => {
        if (node._videoObj && node._videoObj.style && !canvasState.visibleNodes.has(node)) {
            node._videoObj.style.display = 'none';
        }
        if (node._iframeObj && node._iframeObj.style && !canvasState.visibleNodes.has(node)) {
            node._iframeObj.style.display = 'none';
        }
    });

    // Update tooltip position for selected nodes
    if (uiManager) {
        uiManager.updateGenerateIdeasTooltip();
        uiManager.updateSelectionStatus();
    }
}

export function drawGrid(ctx, offsetX, offsetY, scale, canvasWidth, canvasHeight) {
    const gridSize = 40;
    const startX = Math.floor(-offsetX / scale / gridSize) * gridSize;
    const startY = Math.floor(-offsetY / scale / gridSize) * gridSize;
    const endX = startX + (canvasWidth / scale) + gridSize;
    const endY = startY + (canvasHeight / scale) + gridSize;
    
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.5;
    
    for (let x = startX; x < endX; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, startY);
        ctx.lineTo(x, endY);
        ctx.stroke();
    }
    
    for (let y = startY; y < endY; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(startX, y);
        ctx.lineTo(endX, y);
        ctx.stroke();
    }
    
    ctx.globalAlpha = 1;
}

export function wrapText(ctx, text, maxWidth) {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';
    
    words.forEach(word => {
        const testLine = currentLine + (currentLine ? ' ' : '') + word;
        const metrics = ctx.measureText(testLine);
        
        if (metrics.width > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
        } else {
            currentLine = testLine;
        }
    });
    
    if (currentLine) {
        lines.push(currentLine);
    }
    
    return lines;
}

export function drawNode(
    ctx,
    node,
    showConnectionPoints = false,
    showResizeHandles = true,
    hoverInfo = null,
    options = {}
) {
    const { isActiveThreadNode = false, isThreadRoot = false } = options || {};
    if (node.isMediaReference) {
        ctx.save(); // Save context state before drawing media reference node

        const headerHeight = 50; // Increased from 30px to 50px for easier dragging
        
        // Draw header/handle area at the top
        ctx.fillStyle = node.isSelected ? '#2d3748' : '#1a202c';
        ctx.fillRect(node.x, node.y, node.width, headerHeight);
        
        // Draw header border
        const baseBorderColor = node.isSelected
            ? '#4299e1'
            : isThreadRoot
                ? '#f59e0b'
                : isActiveThreadNode
                    ? '#6366f1'
                    : '#4a5568';
        const baseBorderWidth = node.isSelected ? 2 : isThreadRoot ? 3 : isActiveThreadNode ? 2 : 1;
        ctx.strokeStyle = node.isSelected ? '#4299e1' : '#4a5568';
        ctx.lineWidth = node.isSelected ? 2 : 1;
        ctx.strokeRect(node.x, node.y, node.width, headerHeight);

        // Add drag handle icon/text in the header - better positioning
        ctx.fillStyle = '#9ca3af';
        ctx.font = '14px Arial'; // Slightly larger font
        
        // Display node name/text if it exists, otherwise show drag handle
        const displayText = (node.name && node.name.trim() !== '') ? node.name : 
                           (node.text && node.text.trim() !== '') ? node.text : null;
        
        if (displayText) {
            ctx.fillStyle = '#e2e8f0';
            ctx.font = 'bold 12px Arial';
            const nameText = displayText.length > 25 ? displayText.substring(0, 25) + '...' : displayText;
            ctx.fillText(nameText, node.x + 8, node.y + 16);
            
            // Show drag handle icon on the right
            ctx.fillStyle = '#9ca3af';
            ctx.font = '12px Arial';
            ctx.fillText('⋮⋮', node.x + node.width - 25, node.y + 16);
        } else {
            // Show default drag handle text centered
            ctx.textAlign = 'center';
            ctx.fillText('⋮⋮ Media Node ⋮⋮', node.x + node.width / 2, node.y + 16);
            ctx.textAlign = 'left'; // Reset alignment
        }
        
        // Additional drag hint
        ctx.fillStyle = '#6b7280';
        ctx.font = '10px Arial';
        const hintText = displayText ? 'Double-click to edit name' : 'Double-click to add name';
        ctx.fillText(hintText, node.x + 8, node.y + 35);
        
        // Draw selection border for the entire node if selected
        if (node.isSelected) {
            ctx.strokeStyle = '#4299e1';
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 4]);
            ctx.strokeRect(node.x, node.y, node.width, node.height);
            ctx.setLineDash([]); // Reset dash pattern
        } else if (isThreadRoot || isActiveThreadNode) {
            ctx.strokeStyle = baseBorderColor;
            ctx.lineWidth = baseBorderWidth;
            ctx.strokeRect(node.x, node.y, node.width, node.height);
        }

        // Draw connection points for media nodes when requested
        if (showConnectionPoints) {
            drawConnectionPoints(ctx, node, hoverInfo);
        }

        // Draw resize handles for media nodes when requested
        if (showResizeHandles && node.isSelected) {
            drawResizeHandles(ctx, node);
        }
        
        ctx.restore(); // Restore context state
        
        // Store header height for interaction detection
        node._headerHeight = headerHeight;
        return;
    }
    const { x, y, width, text, isSelected, generatedByModel, maxHeight = 400, scrollY = 0 } = node;

    // Enhanced margin configuration for better text spacing
    const margins = {
        top: 32,    // Increased from 28 to 32 for better grabbability and visual balance
        bottom: 12, // Bottom margin
        left: 12,   // Left margin
        right: 12   // Right margin
    };

    const minHeight = 80; // Increased from 60 to 80 to align with new default height

    // Calculate required height based on markdown content with new margins
    const fontSizeForMeasurement = node.fontSize || DEFAULT_GLOBAL_FONT_SIZE;
    const requiredHeight = Math.max(
        minHeight,
        calculateMarkdownHeight(ctx, text, width, margins, fontSizeForMeasurement)
    );

    // Only auto-adjust height if node hasn't been manually resized, but respect maxHeight
    if (!node.manuallyResized) {
        node.height = Math.min(requiredHeight, maxHeight);
    }

    const height = node.height;

    // Store the full content height for scrolling calculations
    node.contentHeight = requiredHeight;

    // Draw model attribution above the node if it was generated by AI
    if (generatedByModel) {
        ctx.save();
        ctx.font = '14px Arial';
        ctx.fillStyle = '#9ca3af';
        ctx.textAlign = 'center';

        // Position the text above the node
        const modelText = `🤖 ${generatedByModel}`;
        const textX = x + width / 2;
        const textY = y - 10;

        // Draw a small background for better readability
        const textMetrics = ctx.measureText(modelText);
        const textWidth = textMetrics.width;
        const textHeight = 16;

        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(textX - textWidth/2 - 4, textY - textHeight + 2, textWidth + 8, textHeight);

        ctx.fillStyle = '#9ca3af';
        ctx.fillText(modelText, textX, textY);
        ctx.restore();
    }

    // Draw node background
    ctx.fillStyle = isSelected ? '#2d3748' : '#1a202c';
    ctx.fillRect(x, y, width, height);

    // Draw node border
    const borderColor = isSelected
        ? '#4299e1'
        : isThreadRoot
            ? '#f59e0b'
            : isActiveThreadNode
                ? '#6366f1'
                : '#4a5568';
    const borderWidth = isSelected ? 2 : isThreadRoot ? 3 : isActiveThreadNode ? 2 : 1;
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    ctx.strokeRect(x, y, width, height);

    // Draw connection points first if requested (they should be most prominent)
    if (showConnectionPoints) {
        drawConnectionPoints(ctx, node, hoverInfo);
    }

    // Draw resize handles based on the showResizeHandles parameter
    // This reduces visual clutter and prioritizes connection functionality
    if (showResizeHandles && isSelected) {
        drawResizeHandles(ctx, node);
    }

    // Render markdown content with enhanced margins and scrolling support
    renderMarkdownToCanvas(
        ctx,
        text,
        x,
        y,
        width,
        height,
        margins,
        node.scrollY || 0,
        fontSizeForMeasurement
    );

    // Draw scroll indicator if content is scrollable and node is selected
    if (node.contentHeight > height && isSelected) {
        drawScrollIndicator(ctx, node);
    }
}

export function drawScrollIndicator(ctx, node) {
    const { x, y, width, height, contentHeight, scrollY = 0 } = node;

    if (contentHeight <= height) return; // No scrolling needed

    // Calculate scrollbar dimensions
    const scrollbarWidth = 8;
    const scrollbarX = x + width - scrollbarWidth - 4;
    const scrollbarY = y + 4;
    const scrollbarHeight = height - 8;

    // Calculate thumb position and size
    const thumbHeight = Math.max(20, (height / contentHeight) * scrollbarHeight);
    const maxScrollY = contentHeight - height;
    const thumbY = scrollbarY + (scrollY / maxScrollY) * (scrollbarHeight - thumbHeight);

    // Draw scrollbar track
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.fillRect(scrollbarX, scrollbarY, scrollbarWidth, scrollbarHeight);

    // Draw scrollbar thumb
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.fillRect(scrollbarX, thumbY, scrollbarWidth, thumbHeight);
}

export function drawConnectionPoints(ctx, node, hoverInfo = null) {
    ctx.save(); // Save context state
    
    const { x, y, width, height } = node;
    const pointRadius = 12; // Optimized size for organized connectors

    // Calculate connection point positions - exactly 4 points
    const points = getConnectionPoints(node);

    // Draw each connection point with professional styling
    points.forEach((point) => {
        // Check if this specific connection point is being hovered
        const isHovered = hoverInfo && hoverInfo.hoveredConnectionPoint && 
                         hoverInfo.hoveredConnectionPoint.side === point.side;
        
        // Enhance visual feedback for hovered connection points
        const currentRadius = isHovered ? pointRadius + 4 : pointRadius;
        const shadowIntensity = isHovered ? 1.0 : 0.6;
        const shadowBlurRadius = isHovered ? 20 : 12;
        
        // Create organized connector appearance with enhanced hover states
        ctx.shadowColor = `rgba(34, 197, 94, ${shadowIntensity})`;
        ctx.shadowBlur = shadowBlurRadius;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        // Outer ring - different colors for each side for better organization
        const sideColors = {
            'top': '#10b981',    // Emerald green
            'right': '#3b82f6',  // Blue  
            'bottom': '#f59e0b', // Amber
            'left': '#ef4444'    // Red
        };
        
        ctx.fillStyle = sideColors[point.side] || '#10b981';
        ctx.strokeStyle = isHovered ? '#fbbf24' : '#ffffff'; // Golden highlight when hovered
        ctx.lineWidth = isHovered ? 4 : 3;

        ctx.beginPath();
        ctx.arc(point.x, point.y, currentRadius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();

        // Inner circle for depth - enhanced when hovered
        ctx.shadowBlur = 0; // Remove shadow for inner elements
        ctx.fillStyle = isHovered ? 'rgba(255, 255, 255, 1.0)' : 'rgba(255, 255, 255, 0.9)';
        ctx.beginPath();
        ctx.arc(point.x, point.y, currentRadius - 4, 0, 2 * Math.PI);
        ctx.fill();

        // Center indicator dot - larger when hovered
        ctx.fillStyle = sideColors[point.side] || '#10b981';
        ctx.beginPath();
        ctx.arc(point.x, point.y, isHovered ? 4 : 3, 0, 2 * Math.PI);
        ctx.fill();

        // Add directional indicator (small arrow or line)
        ctx.strokeStyle = sideColors[point.side] || '#10b981';
        ctx.lineWidth = 2;
        ctx.beginPath();
        
        // Draw directional indicator based on side
        switch(point.side) {
            case 'top':
                ctx.moveTo(point.x, point.y - 6);
                ctx.lineTo(point.x, point.y - 2);
                break;
            case 'right': 
                ctx.moveTo(point.x + 6, point.y);
                ctx.lineTo(point.x + 2, point.y);
                break;
            case 'bottom':
                ctx.moveTo(point.x, point.y + 6);
                ctx.lineTo(point.x, point.y + 2);
                break;
            case 'left':
                ctx.moveTo(point.x - 6, point.y);
                ctx.lineTo(point.x - 2, point.y);
                break;
        }
        ctx.stroke();
    });

    ctx.restore(); // Restore context state
}

// Function to draw visual resize handles on selected nodes
export function drawResizeHandles(ctx, node) {
    ctx.save(); // Save context state
    
    const { x, y, width, height } = node;
    const handleSize = 12; // Slightly smaller to be less prominent
    const handleColor = '#6b7280'; // More subtle gray color
    const handleBorderColor = '#9ca3af'; // Light gray border for subtlety

    // Calculate handle positions
    const handles = [
        { x: x - handleSize/2, y: y - handleSize/2, type: 'top-left' },           // Top-left
        { x: x + width/2 - handleSize/2, y: y - handleSize/2, type: 'top' },      // Top-center
        { x: x + width - handleSize/2, y: y - handleSize/2, type: 'top-right' },  // Top-right
        { x: x + width - handleSize/2, y: y + height/2 - handleSize/2, type: 'right' }, // Right-center
        { x: x + width - handleSize/2, y: y + height - handleSize/2, type: 'bottom-right' }, // Bottom-right
        { x: x + width/2 - handleSize/2, y: y + height - handleSize/2, type: 'bottom' }, // Bottom-center
        { x: x - handleSize/2, y: y + height - handleSize/2, type: 'bottom-left' }, // Bottom-left
        { x: x - handleSize/2, y: y + height/2 - handleSize/2, type: 'left' }     // Left-center
    ];

    // Draw each handle with subtle styling
    handles.forEach(handle => {
        // Subtle shadow for depth
        ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;

        // Draw handle background with subtle styling
        ctx.fillStyle = handleColor;
        ctx.strokeStyle = handleBorderColor;
        ctx.lineWidth = 2; // Thinner border for subtlety

        ctx.fillRect(handle.x, handle.y, handleSize, handleSize);
        ctx.strokeRect(handle.x, handle.y, handleSize, handleSize);
    });

    ctx.restore(); // Restore context state
}

// Helper function to check if a point is over a resize handle
export function getResizeHandleAt(node, x, y) {
    const handleSize = 14; // Match the increased visual size
    const tolerance = 8; // Much larger tolerance for very easy clicking
    const effectiveSize = handleSize + tolerance * 2;

    // Calculate handle positions (same as in drawResizeHandles)
    const handles = [
        { x: node.x - handleSize/2, y: node.y - handleSize/2, type: 'top-left' },
        { x: node.x + node.width/2 - handleSize/2, y: node.y - handleSize/2, type: 'top' },
        { x: node.x + node.width - handleSize/2, y: node.y - handleSize/2, type: 'top-right' },
        { x: node.x + node.width - handleSize/2, y: node.y + node.height/2 - handleSize/2, type: 'right' },
        { x: node.x + node.width - handleSize/2, y: node.y + node.height - handleSize/2, type: 'bottom-right' },
        { x: node.x + node.width/2 - handleSize/2, y: node.y + node.height - handleSize/2, type: 'bottom' },
        { x: node.x - handleSize/2, y: node.y + node.height - handleSize/2, type: 'bottom-left' },
        { x: node.x - handleSize/2, y: node.y + node.height/2 - handleSize/2, type: 'left' }
    ];

    // Check if click is within any handle with generous tolerance
    for (const handle of handles) {
        if (x >= handle.x - tolerance && x <= handle.x + effectiveSize &&
            y >= handle.y - tolerance && y <= handle.y + effectiveSize) {
            return handle.type;
        }
    }

    return null;
}

// Helper function to get connection point coordinates
export function getConnectionPoints(node) {
    const { x, y, width, height } = node;

    return [
        { x: x + width / 2, y: y, side: 'top' },           // Top center
        { x: x + width, y: y + height / 2, side: 'right' }, // Right center
        { x: x + width / 2, y: y + height, side: 'bottom' }, // Bottom center
        { x: x, y: y + height / 2, side: 'left' }          // Left center
    ];
}

// Helper function to check if a point is over a connection point
export function getConnectionPointAt(node, x, y) {
    const points = getConnectionPoints(node);
    const pointRadius = 16; // Adjusted detection radius for organized connectors (12 + 4 tolerance)

    for (const point of points) {
        const distance = Math.sqrt(Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2));
        if (distance <= pointRadius) {
            return point;
        }
    }
    return null;
}

// Helper function to calculate intersection of line with rectangle
function getLineRectangleIntersection(centerX, centerY, targetX, targetY, rectX, rectY, rectWidth, rectHeight) {
    // Calculate direction vector
    const dx = targetX - centerX;
    const dy = targetY - centerY;

    // If no direction, return center
    if (dx === 0 && dy === 0) {
        return { x: centerX, y: centerY };
    }

    // Calculate intersections with all four edges
    const intersections = [];

    // Left edge (x = rectX)
    if (dx !== 0) {
        const t = (rectX - centerX) / dx;
        const y = centerY + t * dy;
        if (t > 0 && y >= rectY && y <= rectY + rectHeight) {
            intersections.push({ x: rectX, y: y, distance: t });
        }
    }

    // Right edge (x = rectX + rectWidth)
    if (dx !== 0) {
        const t = (rectX + rectWidth - centerX) / dx;
        const y = centerY + t * dy;
        if (t > 0 && y >= rectY && y <= rectY + rectHeight) {
            intersections.push({ x: rectX + rectWidth, y: y, distance: t });
        }
    }

    // Top edge (y = rectY)
    if (dy !== 0) {
        const t = (rectY - centerY) / dy;
        const x = centerX + t * dx;
        if (t > 0 && x >= rectX && x <= rectX + rectWidth) {
            intersections.push({ x: x, y: rectY, distance: t });
        }
    }

    // Bottom edge (y = rectY + rectHeight)
    if (dy !== 0) {
        const t = (rectY + rectHeight - centerY) / dy;
        const x = centerX + t * dx;
        if (t > 0 && x >= rectX && x <= rectX + rectWidth) {
            intersections.push({ x: x, y: rectY + rectHeight, distance: t });
        }
    }

    // Return the closest intersection
    if (intersections.length > 0) {
        const closest = intersections.reduce((min, curr) =>
            curr.distance < min.distance ? curr : min
        );
        return { x: closest.x, y: closest.y };
    }

    // Fallback to center if no intersection found
    return { x: centerX, y: centerY };
}

export function drawConnection(ctx, connection, nodes, isSelected = false) {
    // Handle both old and new connection formats during transition
    const fromNodeId = connection.fromNode || connection.from;
    const toNodeId = connection.toNode || connection.to;
    
    // Skip connections with missing data
    if (!fromNodeId || !toNodeId) return;
    
    const fromNode = nodes.find(n => n.id && n.id.toString() === fromNodeId.toString());
    const toNode = nodes.find(n => n.id && n.id.toString() === toNodeId.toString());

    if (!fromNode || !toNode) return;

    // Get organized connection points for both nodes
    const fromPoints = getConnectionPoints(fromNode);
    const toPoints = getConnectionPoints(toNode);

    // Find the best connection points (closest pair)
    let bestFromPoint = fromPoints[0];
    let bestToPoint = toPoints[0]; 
    let shortestDistance = Infinity;

    // Use stored connection points if available, otherwise find the closest pair
    const fromSide = connection.fromSide || connection.fromPoint;
    const toSide = connection.toSide || connection.toPoint;
    
    if (fromSide && toSide) {
        // Use specific connection points if they were stored
        bestFromPoint = fromPoints.find(p => p.side === fromSide) || fromPoints[0];
        bestToPoint = toPoints.find(p => p.side === toSide) || toPoints[0];
    } else {
        // Find the closest connection points automatically
        fromPoints.forEach(fromPoint => {
            toPoints.forEach(toPoint => {
                const distance = Math.sqrt(
                    Math.pow(toPoint.x - fromPoint.x, 2) + 
                    Math.pow(toPoint.y - fromPoint.y, 2)
                );
                if (distance < shortestDistance) {
                    shortestDistance = distance;
                    bestFromPoint = fromPoint;
                    bestToPoint = toPoint;
                }
            });
        });
    }

    // Calculate angle for arrow head using the organized connection points
    const angle = Math.atan2(bestToPoint.y - bestFromPoint.y, bestToPoint.x - bestFromPoint.x);

    // Calculate arrow position with offset to ensure visibility
    const arrowOffset = 16; // Adjusted for organized connectors
    const arrowX = bestToPoint.x - Math.cos(angle) * arrowOffset;
    const arrowY = bestToPoint.y - Math.sin(angle) * arrowOffset;

    // Draw line with enhanced styling - different colors for selected state
    if (isSelected) {
        ctx.strokeStyle = '#2196f3'; // Blue for selected
        ctx.lineWidth = 4; // Thicker when selected
    } else {
        ctx.strokeStyle = '#a0a0a0'; // Light gray for better aesthetics with dark theme
        ctx.lineWidth = 3;
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Add subtle shadow for depth
    if (isSelected) {
        ctx.shadowColor = 'rgba(33, 150, 243, 0.4)';
    } else {
        ctx.shadowColor = 'rgba(128, 128, 128, 0.3)';
    }
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;

    ctx.beginPath();
    ctx.moveTo(bestFromPoint.x, bestFromPoint.y);
    ctx.lineTo(arrowX, arrowY);
    ctx.stroke();

    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    // Draw arrowhead at the offset position with selection state
    drawArrowhead(ctx, arrowX, arrowY, angle, isSelected);
}

export function drawArrowhead(ctx, x, y, angle, isSelected = false) {
    const arrowLength = 18; // Larger arrow for organized connectors
    const arrowAngle = Math.PI / 6; // Narrower angle (30 degrees) for cleaner appearance

    // Enhanced shadow for the arrowhead
    if (isSelected) {
        ctx.shadowColor = 'rgba(33, 150, 243, 0.6)';
    } else {
        ctx.shadowColor = 'rgba(128, 128, 128, 0.5)';
    }
    ctx.shadowBlur = 6;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;

    // Professional arrow colors - consistent with organized connector theme
    if (isSelected) {
        ctx.fillStyle = '#2196f3'; // Bright blue for selected
        ctx.strokeStyle = '#1565c0'; // Darker blue border
    } else {
        ctx.fillStyle = '#6b7280'; // Professional gray
        ctx.strokeStyle = '#374151'; // Darker gray border
    }
    ctx.lineWidth = 2;

    // Draw filled arrow with clean geometry
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(
        x - arrowLength * Math.cos(angle - arrowAngle),
        y - arrowLength * Math.sin(angle - arrowAngle)
    );
    ctx.lineTo(
        x - arrowLength * 0.6 * Math.cos(angle), // Create slight indentation for better arrow shape
        y - arrowLength * 0.6 * Math.sin(angle)
    );
    ctx.lineTo(
        x - arrowLength * Math.cos(angle + arrowAngle),
        y - arrowLength * Math.sin(angle + arrowAngle)
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
}

export function drawConnectionPreview(ctx, connectionStart, lastMouseX, lastMouseY, offsetX, offsetY, scale, connectionPoint = null) {
    if (!connectionStart) return;

    const canvasX = (lastMouseX - offsetX) / scale;
    const canvasY = (lastMouseY - offsetY) / scale;

    // Use connection point if provided, otherwise use node center
    let fromX, fromY;
    if (connectionPoint) {
        fromX = connectionPoint.x;
        fromY = connectionPoint.y;
    } else {
        fromX = connectionStart.x + connectionStart.width / 2;
        fromY = connectionStart.y + connectionStart.height / 2;
    }

    // Draw dashed preview line
    ctx.strokeStyle = '#2196f3';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.lineTo(canvasX, canvasY);
    ctx.stroke();
    ctx.setLineDash([]);
}

// Helper function to calculate distance from a point to a line segment
function distanceToLineSegment(px, py, x1, y1, x2, y2) {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;

    if (lenSq === 0) {
        // Line segment is actually a point
        return Math.sqrt(A * A + B * B);
    }

    let param = dot / lenSq;

    let xx, yy;

    if (param < 0) {
        xx = x1;
        yy = y1;
    } else if (param > 1) {
        xx = x2;
        yy = y2;
    } else {
        xx = x1 + param * C;
        yy = y1 + param * D;
    }

    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
}

// Helper function to check if a point is on a connection
export function getConnectionAtPoint(x, y, connections, nodes, tolerance = 8) {
    for (const connection of connections) {
        // Handle both old and new connection formats during transition
        const fromNodeId = connection.fromNode || connection.from;
        const toNodeId = connection.toNode || connection.to;
        
        // Skip connections with missing data
        if (!fromNodeId || !toNodeId) continue;
        
        const fromNode = nodes.find(n => n.id && n.id.toString() === fromNodeId.toString());
        const toNode = nodes.find(n => n.id && n.id.toString() === toNodeId.toString());

        if (!fromNode || !toNode) continue;

        // Get organized connection points for both nodes
        const fromPoints = getConnectionPoints(fromNode);
        const toPoints = getConnectionPoints(toNode);

        // Find the best connection points (same logic as drawConnection)
        let bestFromPoint = fromPoints[0];
        let bestToPoint = toPoints[0]; 
        let shortestDistance = Infinity;

        // Use stored connection points if available, otherwise find the closest pair
        const fromSide = connection.fromSide || connection.fromPoint;
        const toSide = connection.toSide || connection.toPoint;
        
        if (fromSide && toSide) {
            bestFromPoint = fromPoints.find(p => p.side === fromSide) || fromPoints[0];
            bestToPoint = toPoints.find(p => p.side === toSide) || toPoints[0];
        } else {
            // Find the closest connection points automatically
            fromPoints.forEach(fromPoint => {
                toPoints.forEach(toPoint => {
                    const distance = Math.sqrt(
                        Math.pow(toPoint.x - fromPoint.x, 2) + 
                        Math.pow(toPoint.y - fromPoint.y, 2)
                    );
                    if (distance < shortestDistance) {
                        shortestDistance = distance;
                        bestFromPoint = fromPoint;
                        bestToPoint = toPoint;
                    }
                });
            });
        }

        // Calculate arrow position with offset
        const angle = Math.atan2(bestToPoint.y - bestFromPoint.y, bestToPoint.x - bestFromPoint.x);
        const arrowOffset = 16;
        const arrowX = bestToPoint.x - Math.cos(angle) * arrowOffset;
        const arrowY = bestToPoint.y - Math.sin(angle) * arrowOffset;

        // Check if point is close to the line segment using organized connection points
        const distance = distanceToLineSegment(x, y, bestFromPoint.x, bestFromPoint.y, arrowX, arrowY);

        if (distance <= tolerance) {
            return connection;
        }
    }

    return null;
}

export function drawSelectionRectangle(ctx, selectionStart, selectionEnd) {
    if (!selectionStart || !selectionEnd) return;

    const x = Math.min(selectionStart.x, selectionEnd.x);
    const y = Math.min(selectionStart.y, selectionEnd.y);
    const width = Math.abs(selectionEnd.x - selectionStart.x);
    const height = Math.abs(selectionEnd.y - selectionStart.y);

    // Draw selection rectangle background
    ctx.fillStyle = 'rgba(66, 153, 225, 0.1)';
    ctx.fillRect(x, y, width, height);

    // Draw selection rectangle border
    ctx.strokeStyle = '#4299e1';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    ctx.strokeRect(x, y, width, height);
    ctx.setLineDash([]);
}